/* ============================================================================
   Subtle Energies — Constitution Quiz
   Production script for Shopify.

   Contract: quiz-lead-api-spec.md v1.0.

   Everything lives inside one IIFE and is initialised per root element, so the
   file can be loaded once and survives the theme editor re-rendering the
   section. No globals are created, and no inline `onclick` handlers are used —
   all interaction is delegated from the root node.
   ========================================================================== */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var REQUEST_TIMEOUT_MS = 12000;
  var RETRY_DELAY_MS = 1200;
  var TRANSITION_MS = 580;
  var MAX_QUESTIONS = 14;

  /* ── result content model ─────────────────────────────────────────────── */

  var M = {
    vata: {
      element: 'Air &amp; Ether',
      hero: 'Air<br>&amp; Ether',
      sanskrit: 'Vata',
      pillLabel: 'Air · Ether',
      dominantTraits: 'a light, quick and instinctively creative constitution — a mind that moves fast, adapts easily and senses change before others do',
      subTraits: 'a quiet quickness and imagination beneath the surface, surfacing as sudden bursts of insight or restlessness',
      skinGood: 'fine in texture and naturally delicate, glowing when well-nourished',
      skinImbalance: 'dryness, roughness and a tendency to feel thin or under-nourished',
      allies: 'warming, deeply nourishing and grounding actives — the kind that calm and replenish',
      emotion: 'you feel quickly and adapt easily, thriving on variety, though under strain your mind can race and your energy scatter'
    },
    pitta: {
      element: 'Fire &amp; Water',
      hero: 'Fire<br>&amp; Water',
      sanskrit: 'Pitta',
      pillLabel: 'Fire · Water',
      dominantTraits: 'a sharp, driven and articulate constitution — natural focus, clarity and the kind of intensity that gets things done',
      subTraits: 'a simmering intensity and drive beneath the surface, surfacing as sudden sharpness or a pull to lead',
      skinGood: 'warm-toned and reactive, with a natural glow when balanced',
      skinImbalance: 'sensitivity, redness and a tendency toward inflammation or breakouts',
      allies: 'cooling, soothing and calming actives — the kind that reduce heat and settle reactivity',
      emotion: 'you are passionate, decisive and direct, though under strain you can become irritable or overheated'
    },
    kapha: {
      element: 'Water &amp; Earth',
      hero: 'Water<br>&amp; Earth',
      sanskrit: 'Kapha',
      pillLabel: 'Water · Earth',
      dominantTraits: 'a naturally strong, grounded and enduring constitution — a steadiness others are drawn to',
      subTraits: 'a quiet steadiness and loyalty beneath the surface, surfacing as calm reserves of patience and resilience',
      skinGood: 'lustrous, plump and cool to the touch, robust and slow to age',
      skinImbalance: 'congestion, oiliness and a tendency toward heaviness',
      allies: 'stimulating, warming and purifying actives — the kind that move and energise rather than sedate',
      emotion: 'you are the calm in the room — loyal, consistent and long in memory, though under strain you can become withdrawn and heavy'
    }
  };

  var ENERGY_LABELS = {
    1: 'Low — energetically drained, needing recharge',
    2: 'Somewhat low',
    3: 'Okay / Balanced',
    4: 'Somewhat good',
    5: 'Great — energetically charged'
  };

  var PREDICT_LABELS = {
    vata: 'Creative, adaptable, intuitive — mind moves fast, thrives on change.',
    pitta: 'Focused, driven, purposeful — sharp clarity, natural intensity.',
    kapha: 'Grounded, steady, nurturing — calm presence, enduring strength.'
  };

  /* ── helpers ──────────────────────────────────────────────────────────── */

  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  function text(el) {
    return el ? String(el.textContent).replace(/\s+/g, ' ').trim() : '';
  }

  /**
   * RFC 4122 v4 UUID. The Worker rejects anything that does not match its
   * UUID regex with `400 invalid_submission_id`, so every branch here — including
   * the last-resort one — must produce a real v4 string.
   */
  function uuidV4() {
    var c = typeof crypto !== 'undefined' ? crypto : null;
    if (c && typeof c.randomUUID === 'function') {
      try { return c.randomUUID(); } catch (e) { /* fall through */ }
    }
    var bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') {
      try { c.getRandomValues(bytes); } catch (e) { bytes = null; }
    } else {
      bytes = null;
    }
    if (!bytes) {
      bytes = new Uint8Array(16);
      for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    var hex = [];
    for (var j = 0; j < 16; j++) hex.push((bytes[j] + 0x100).toString(16).slice(1));
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }

  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* ── init ─────────────────────────────────────────────────────────────── */

  function initQuiz(root) {
    if (!root || root.dataset.seqReady === '1') return;
    root.dataset.seqReady = '1';

    var endpoint = (root.dataset.endpoint || '').trim();
    var quizVersion = (root.dataset.quizVersion || '1.0').trim();
    var isStandalone = root.dataset.layoutMode === 'standalone';
    var debug = root.dataset.debug === 'true';
    var MSG_REQUIRED = root.dataset.msgRequired || 'This field is required.';
    var MSG_INVALID = root.dataset.msgInvalid || 'This email is invalid.';

    var screens = {};
    var order = [];
    Array.prototype.forEach.call(root.querySelectorAll('[data-screen]'), function (el) {
      screens[el.dataset.screen] = el;
      order.push(el.dataset.screen);
    });
    if (!order.length) return;

    var current = order[0];
    var animating = false;

    var state = {
      firstName: '',
      email: '',
      energyLevel: 3,
      selfPrediction: null,
      marketingConsent: false
    };

    /* answers[questionKey] = array of {dosha, label} in click order */
    var answers = {};
    var submissionId = null;
    var submitted = false;

    /* ── stage sizing ───────────────────────────────────────────────────── */

    // The quiz is given ONE definite height. Everything inside it then fits or
    // scrolls within that height, which is what removes the second scrollbar.
    function sizeStage() {
      if (isStandalone) {
        root.style.setProperty('--seq-offset', '0px');
        return;
      }
      var viewport = window.innerHeight || 0;
      var offset = 0;

      // How far down the page the section starts — covers a normal (non-overlay)
      // theme header.
      var rect = root.getBoundingClientRect();
      var docTop = rect.top + (window.scrollY || window.pageYOffset || 0);
      if (docTop > 0) offset = docTop;

      // A sticky/fixed header overlays the section instead of pushing it down,
      // so its height has to be subtracted separately.
      var header = document.querySelector(
        root.dataset.headerSelector ||
        '#shopify-section-header, .shopify-section-header, header[role="banner"], .header-wrapper, .site-header'
      );
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') {
          offset = Math.max(offset, header.getBoundingClientRect().height);
        }
      }

      // Never let the offset eat the quiz: 40% of the viewport is the floor.
      offset = Math.max(0, Math.min(Math.round(offset), Math.round(viewport * 0.4)));
      root.style.setProperty('--seq-offset', offset + 'px');
    }

    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sizeStage, 120);
    }

    sizeStage();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(sizeStage).catch(function () {});
    }

    /* ── navigation ─────────────────────────────────────────────────────── */

    function setInert(el, on) {
      if (on) {
        el.setAttribute('aria-hidden', 'true');
        if ('inert' in el) el.inert = true;
      } else {
        el.removeAttribute('aria-hidden');
        if ('inert' in el) el.inert = false;
      }
    }

    Object.keys(screens).forEach(function (key) {
      setInert(screens[key], key !== current);
      screens[key].setAttribute('tabindex', '-1');
    });

    function go(next) {
      if (animating || !screens[next] || next === current) return;
      var forward = order.indexOf(next) > order.indexOf(current);
      var cur = screens[current];
      var nxt = screens[next];

      animating = true;
      cur.classList.add('is-animating');
      nxt.classList.add('is-animating');
      setInert(nxt, false);

      // Every screen starts its life at the top. Without this, coming back to a
      // screen you had scrolled leaves you mid-content with no visible heading.
      nxt.scrollTop = 0;
      nxt.classList.remove('is-current', 'is-exiting');

      // Park the incoming screen off-stage with the transition suppressed, then
      // force a reflow so the browser commits that as the animation's start
      // value. This used to be a nested requestAnimationFrame — but rAF is
      // paused entirely in a background tab, so switching tabs mid-slide left
      // `animating` stuck true and froze the quiz for the rest of the session.
      nxt.style.transition = 'none';
      nxt.style.transform = forward ? 'translateY(100%)' : 'translateY(-100%)';
      void nxt.offsetHeight;
      nxt.style.transition = '';

      cur.classList.remove('is-current');
      cur.style.transform = forward ? 'translateY(-100%)' : 'translateY(100%)';
      nxt.classList.add('is-current');
      nxt.style.transform = '';

      // Scheduled synchronously, so the lock always releases.
      setTimeout(function () {
        cur.classList.remove('is-animating', 'is-exiting');
        nxt.classList.remove('is-animating');
        setInert(cur, true);
        current = next;
        animating = false;
        try { nxt.focus({ preventScroll: true }); } catch (e) { nxt.focus(); }
      }, TRANSITION_MS);
    }

    /* ── scoring ────────────────────────────────────────────────────────── */

    function computeTally() {
      var tally = { vata: 0, pitta: 0, kapha: 0 };
      Object.keys(answers).forEach(function (q) {
        answers[q].forEach(function (a) { tally[a.dosha] += 1; });
      });
      return tally;
    }

    function getDominance() {
      var tally = computeTally();
      // Array.prototype.sort is stable, so an exact tie keeps this priority order.
      var sorted = ['vata', 'pitta', 'kapha'].slice().sort(function (a, b) {
        return tally[b] - tally[a];
      });
      var tridoshic = tally.vata === tally.pitta && tally.pitta === tally.kapha;
      return { dominant: sorted[0], subdominant: sorted[1], tally: tally, tridoshic: tridoshic };
    }

    /* ── options ────────────────────────────────────────────────────────── */

    function syncGroup(group) {
      var q = group.dataset.question;
      var picked = answers[q] || [];
      group.classList.toggle('is-at-max', picked.length >= 2);

      var continueBtn = root.querySelector('[data-continue-for="' + q + '"]');
      if (continueBtn) continueBtn.classList.toggle('is-visible', picked.length > 0);
    }

    function toggleOption(btn) {
      var group = btn.closest('[data-question]');
      if (!group) return;
      var q = group.dataset.question;
      var dosha = btn.dataset.dosha;
      var label = text(btn.querySelector('.seq-opt-text'));

      if (!answers[q]) answers[q] = [];
      var picked = answers[q];
      var idx = -1;
      for (var i = 0; i < picked.length; i++) {
        if (picked[i].dosha === dosha) { idx = i; break; }
      }

      if (idx >= 0) {
        picked.splice(idx, 1);
        btn.setAttribute('aria-pressed', 'false');
      } else {
        if (picked.length >= 2) return; // hard cap — the Worker rejects 3+
        picked.push({ dosha: dosha, label: label });
        btn.setAttribute('aria-pressed', 'true');
      }

      // Changing an answer makes this a different completed quiz, so the
      // idempotency key has to be reissued — but only while nothing has been
      // successfully stored yet.
      if (!submitted) submissionId = null;
      syncGroup(group);
    }

    function selectPrediction(btn) {
      var group = btn.closest('[data-predict-group]');
      if (!group) return;
      Array.prototype.forEach.call(group.querySelectorAll('[data-predict]'), function (el) {
        el.setAttribute('aria-pressed', String(el === btn));
      });
      state.selfPrediction = btn.dataset.predict;
      if (!submitted) submissionId = null;
      var continueBtn = root.querySelector('[data-continue-for="predict"]');
      if (continueBtn) continueBtn.classList.add('is-visible');
    }

    /* ── result ─────────────────────────────────────────────────────────── */

    function buildResultContent(d) {
      if (d.tridoshic) {
        return {
          pillPrimary: 'Air · Ether',
          pillSecondary: 'Fire · Water · Earth',
          heroHtml: 'Tri-<br>Elemental',
          ayurvedaName: 'Tridoshic in Ayurveda',
          bodyHtml:
            '<p><strong>You are one of the rare few in whom all three elements — Air &amp; Ether, Fire &amp; Water, and Water &amp; Earth — share dominance.</strong> Rather than one force leading, you carry the adaptability of air, the intensity of fire and the steadiness of earth all at once.</p>' +
            '<p>This balance is a gift, though it asks for closer attention: your needs shift more visibly with the seasons, your surroundings and your state of mind than most. Your skin, mood and energy can lean toward any of the three tendencies depending on the day.</p>' +
            '<p>Emotionally, you carry a versatility that lets you meet almost any moment — quick when you need to be, focused when it matters, and steady when things settle.</p>' +
            '<div class="seq-ayur-note">In Ayurveda, this rare equal balance is known as a tridoshic constitution — carrying all three doshas in near-equal measure. Remember that we are all 5 elements — the dosha determines which elements we are born with more predominance and which ones we need to keep in balance and maintain.</div>'
        };
      }

      var dom = M[d.dominant];
      var sub = M[d.subdominant];
      return {
        pillPrimary: dom.pillLabel,
        pillSecondary: sub.pillLabel,
        heroHtml: dom.hero,
        ayurvedaName: dom.sanskrit + ' ' + sub.sanskrit + ' in Ayurveda',
        bodyHtml:
          '<p><strong>' + dom.element + ' are your dominant elements</strong> — giving you ' + dom.dominantTraits + '. ' + sub.element + ' moves beneath the surface as your sub-dominant nature, lending ' + sub.subTraits + '.</p>' +
          '<p>Your skin reflects this duality: at its best, ' + dom.skinGood + '. When out of balance, it tends toward ' + dom.skinImbalance + '. ' + cap(dom.allies) + ' are your greatest allies.</p>' +
          '<p>Emotionally, ' + dom.emotion + '.</p>' +
          '<div class="seq-ayur-note">In Ayurveda, the blueprint is known as prakruti. Here the result is ' + dom.sanskrit + '-' + sub.sanskrit + ', one of three dual constitutions that arise when two or more elements share dominance in our inherited nature or constitution.<br><br>Remember that we are all 5 elements — the dosha determines which elements we are born with and are more predominant in us, as well as what we need to keep in balance and maintain.</div>'
      };
    }

    function set(sel, value, asHtml) {
      var el = root.querySelector(sel);
      if (!el) return;
      // Only ever HTML from the static model above. Anything the customer typed
      // (their first name) goes through textContent.
      if (asHtml) el.innerHTML = value; else el.textContent = value;
    }

    function populateResult() {
      var d = getDominance();
      var content = buildResultContent(d);

      var intu = root.querySelector('[data-intuition-block]');
      if (intu) {
        if (state.selfPrediction) {
          intu.style.display = '';
          set('[data-intuition-value]', PREDICT_LABELS[state.selfPrediction] || '');
        } else {
          intu.style.display = 'none';
        }
      }

      set('[data-result-label]', state.firstName
        ? state.firstName + '’s genetic constitution'
        : 'Your genetic constitution');
      set('[data-pill-primary]', content.pillPrimary);
      set('[data-pill-secondary]', content.pillSecondary);
      set('[data-result-hero]', content.heroHtml, true);
      set('[data-result-name]', 'Dominant elements &nbsp;·&nbsp; <span>' + content.ayurvedaName + '</span>', true);
      set('[data-result-body]', content.bodyHtml, true);
    }

    /* ── payload ────────────────────────────────────────────────────────── */

    function buildAnswersPayload() {
      var out = {};
      for (var i = 1; i <= MAX_QUESTIONS; i++) {
        var key = 'q' + i;
        out[key] = (answers[key] || []).map(function (a) { return a.label; });
      }
      return out;
    }

    function buildPayload() {
      var d = getDominance();
      if (!submissionId) submissionId = uuidV4();

      return {
        submission_id: submissionId,
        email: state.email,
        first_name: state.firstName || '',
        consent_marketing: state.marketingConsent === true,
        // Always a real timestamp. The Worker falls back to its own clock when
        // this is null, which would stamp a consent time onto someone who did
        // not consent.
        consent_at: new Date().toISOString(),
        dominant: d.dominant,
        subdominant: d.tridoshic ? null : d.subdominant,
        tridoshic: d.tridoshic === true,
        result_name: d.tridoshic
          ? 'Tridoshic'
          : M[d.dominant].sanskrit + ' ' + M[d.subdominant].sanskrit,
        tally: { vata: d.tally.vata, pitta: d.tally.pitta, kapha: d.tally.kapha },
        energy_level: parseInt(state.energyLevel, 10) || 3,
        self_prediction: state.selfPrediction || null,
        answers: buildAnswersPayload(),
        quiz_version: quizVersion,
        page_url: String(window.location.href).slice(0, 300),
        website: (root.querySelector('[data-honeypot]') || { value: '' }).value || ''
      };
    }

    /* ── submission ─────────────────────────────────────────────────────── */

    function postLead(payload) {
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = controller
        ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS)
        : null;

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        if (timer) clearTimeout(timer);
        return res.text().then(function (raw) {
          var body = null;
          try { body = JSON.parse(raw); } catch (e) { /* non-JSON body */ }
          return { status: res.status, ok: res.ok, body: body, raw: raw };
        });
      }).catch(function (err) {
        if (timer) clearTimeout(timer);
        // Network failure, CORS rejection or our own timeout. Treated the same
        // as a 502 by the caller: retryable, then a soft failure.
        return { status: 0, ok: false, body: null, raw: '', networkError: err };
      });
    }

    // moveFocus is false when the error is raised by the field losing focus —
    // refocusing there would trap the customer in the input.
    function showFieldError(message, moveFocus) {
      var input = root.querySelector('[data-email-input]');
      var el = root.querySelector('[data-email-error]');
      if (el) {
        if (message) el.textContent = message;
        el.classList.add('is-visible');
      }
      if (input) {
        input.classList.add('is-invalid');
        input.setAttribute('aria-invalid', 'true');
        if (moveFocus !== false) input.focus();
      }
    }

    function clearFieldError() {
      var input = root.querySelector('[data-email-input]');
      var el = root.querySelector('[data-email-error]');
      if (el) el.classList.remove('is-visible');
      if (input) {
        input.classList.remove('is-invalid');
        input.removeAttribute('aria-invalid');
      }
    }

    function showSubmitError(message) {
      var el = root.querySelector('[data-submit-error]');
      if (!el) return;
      if (message) el.textContent = message;
      el.classList.add('is-visible');
    }

    function hideSubmitError() {
      var el = root.querySelector('[data-submit-error]');
      if (el) el.classList.remove('is-visible');
    }

    var submitting = false;

    function handleSubmit(e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (submitting || submitted) return;

      var input = root.querySelector('[data-email-input]');
      var consent = root.querySelector('[data-consent-input]');
      var btn = root.querySelector('[data-submit]');
      if (!input || !btn) return;

      var value = input.value.trim();
      if (value === '') {
        showFieldError(MSG_REQUIRED);
        return;
      }
      if (!EMAIL_RE.test(value)) {
        showFieldError(MSG_INVALID);
        return;
      }

      clearFieldError();
      hideSubmitError();
      state.email = value;
      state.marketingConsent = !!(consent && consent.checked);

      if (!endpoint) {
        // Misconfiguration, not a customer problem: fail loudly in the console,
        // softly on screen.
        console.error('[SE quiz] No endpoint configured. Set it in the section settings.');
        showSubmitError('We were unable to submit your results. Please try again shortly.');
        return;
      }

      var payload = buildPayload();
      var originalLabel = btn.textContent;
      submitting = true;
      btn.disabled = true;
      btn.textContent = 'Sending…';
      input.disabled = true;

      function restore() {
        submitting = false;
        btn.disabled = false;
        btn.textContent = originalLabel;
        input.disabled = false;
      }

      function succeed() {
        submitted = true;
        submitting = false;
        var form = root.querySelector('[data-email-form]');
        var confirm = root.querySelector('[data-confirm]');
        if (form) form.style.display = 'none';
        if (confirm) {
          confirm.classList.add('is-visible');
          confirm.setAttribute('tabindex', '-1');
          try { confirm.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
        }
      }

      // The same submission_id is reused on retry — it is the idempotency key
      // that stops a retry from creating a second Sheet row and a second
      // Klaviyo reading email.
      postLead(payload)
        .then(function (res) {
          if (res.ok) return succeed();

          var code = res.body && res.body.error ? res.body.error : '';

          if (res.status === 400 && code === 'invalid_email') {
            restore();
            showFieldError('That email address was not accepted. Please check it and try again.');
            return;
          }

          if (res.status === 403) {
            console.error(
              '[SE quiz] The lead endpoint rejected this origin (' + window.location.origin + '). ' +
              'It must be added to ALLOWED_ORIGINS in the Cloudflare Worker.'
            );
            restore();
            showSubmitError('We were unable to submit your results from this page. Please try again shortly.');
            return;
          }

          if (res.status >= 400 && res.status < 500) {
            console.error('[SE quiz] Rejected by the lead endpoint:', res.status, res.raw);
            restore();
            showSubmitError('We were unable to submit your results. Please try again shortly.');
            return;
          }

          // 5xx, timeout or network error — retry once, exactly as the spec asks.
          if (debug) console.warn('[SE quiz] Retrying submission', res.status, res.raw);
          return sleep(RETRY_DELAY_MS).then(function () {
            return postLead(payload);
          }).then(function (retry) {
            if (retry.ok) return succeed();
            console.error('[SE quiz] Submission failed after retry:', retry.status, retry.raw);
            restore();
            showSubmitError('We could not save your results just now. Your reading is still on screen — please check your connection and try again.');
          });
        })
        .catch(function (err) {
          console.error('[SE quiz] Unexpected submission error:', err);
          restore();
          showSubmitError('We were unable to submit your results. Please check your connection and try again.');
        });
    }

    /* ── delegated events ───────────────────────────────────────────────── */

    root.addEventListener('click', function (e) {
      // `data-submit` is deliberately absent here: that button is a real
      // `type="submit"` inside `[data-email-form]`, so the form's submit event
      // is the single entry point. Handling the click too would run it twice.
      var el = e.target.closest ? e.target.closest('[data-go],[data-opt],[data-predict],[data-elcard]') : null;
      if (!el || !root.contains(el)) return;

      if (el.hasAttribute('data-opt')) { toggleOption(el); return; }

      if (el.hasAttribute('data-predict')) { selectPrediction(el); return; }

      if (el.hasAttribute('data-elcard')) {
        var group = el.closest('[data-elcards]');
        if (!group) return;
        var cards = group.querySelectorAll('[data-elcard]');
        var wasActive = el.classList.contains('is-active');
        Array.prototype.forEach.call(cards, function (c) {
          c.classList.remove('is-active');
          c.classList.toggle('is-dimmed', !wasActive);
          c.setAttribute('aria-expanded', 'false');
        });
        if (!wasActive) {
          el.classList.add('is-active');
          el.classList.remove('is-dimmed');
          el.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      if (el.hasAttribute('data-go')) {
        var target = el.dataset.go;
        if (target === 'result') populateResult();
        go(target);
      }
    });

    var form = root.querySelector('[data-email-form]');
    if (form) form.addEventListener('submit', handleSubmit);

    var nameInput = root.querySelector('[data-firstname-input]');
    if (nameInput) {
      nameInput.addEventListener('input', function (e) {
        state.firstName = e.target.value.trim().slice(0, 100);
      });
    }

    var slider = root.querySelector('[data-energy-slider]');
    var readout = root.querySelector('[data-energy-readout]');
    if (slider) {
      slider.addEventListener('input', function (e) {
        var v = parseInt(e.target.value, 10);
        if (!(v >= 1 && v <= 5)) v = 3;
        state.energyLevel = v;
        if (readout) readout.textContent = ENERGY_LABELS[v];
        slider.setAttribute('aria-valuetext', ENERGY_LABELS[v]);
      });
    }

    var emailInput = root.querySelector('[data-email-input]');
    if (emailInput) {
      emailInput.addEventListener('input', function () {
        if (emailInput.classList.contains('is-invalid')) clearFieldError();
      });

      // Flag a malformed address as soon as the customer leaves the box, the
      // way the Klaviyo forms do. An empty box stays silent here — "required"
      // belongs to the submit attempt, otherwise merely tabbing past the field
      // scolds someone who has not tried to do anything yet.
      emailInput.addEventListener('blur', function () {
        var value = emailInput.value.trim();
        if (value !== '' && !EMAIL_RE.test(value)) showFieldError(MSG_INVALID, false);
      });
    }

    // Cleanly detach when the theme editor swaps the section out.
    root.seqDestroy = function () {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-se-quiz]');
    Array.prototype.forEach.call(nodes, initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var node = e.target.querySelector ? e.target.querySelector('[data-se-quiz]') : null;
    if (node && typeof node.seqDestroy === 'function') node.seqDestroy();
  });
})();
